import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  List,
  Popconfirm,
  Spin,
  message,
  Empty,
  Tag,
} from 'antd';
import { EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const WorkManageEntityModal = ({
  open,
  onClose,
  title,
  loadItems,
  onCreate,
  onUpdate,
  onDelete,
  nameField = 'name',
  nameLabel = 'نام',
  getItemLabel = (item) => item[nameField] ?? item.category_name ?? item.label_name ?? item.name,
  /** اگر true باشد، آیتم‌های دارای color/label_color به‌صورت badge رنگی نمایش داده می‌شوند */
  coloredBadges = false,
  renderItemLabel,
  extraFormFields = null,
  buildCreatePayload,
  buildUpdatePayload,
}) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const loadItemsRef = useRef(loadItems);
  loadItemsRef.current = loadItems;

  const fetchList = async () => {
    setLoading(true);
    try {
      const list = await loadItemsRef.current();
      setItems(Array.isArray(list) ? list : []);
    } catch {
      message.error('خطا در بارگذاری');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setEditingId(null);
    setEditingName('');
    fetchList();
    // فقط با باز/بسته شدن مودال — بدون وابستگی به loadItems برای جلوگیری از لوپ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAdd = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const payload = buildCreatePayload ? buildCreatePayload(values) : values[nameField];
      await onCreate(payload);
      message.success('ثبت شد');
      form.resetFields();
      await fetchList();
    } catch {
      message.error('خطا در ثبت');
    } finally {
      setSubmitting(false);
    }
  };

  const saveEdit = async (id) => {
    if (!editingName?.trim()) {
      message.warning('نام نمی‌تواند خالی باشد');
      return;
    }
    setEditSaving(true);
    try {
      const payload = buildUpdatePayload
        ? buildUpdatePayload(id, editingName.trim())
        : editingName.trim();
      await onUpdate(id, payload);
      message.success('ویرایش شد');
      setEditingId(null);
      await fetchList();
    } catch {
      message.error('خطا در ویرایش');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await onDelete(id);
      message.success('حذف شد');
      await fetchList();
    } catch {
      message.error('خطا در حذف');
    } finally {
      setDeletingId(null);
    }
  };

  const handleClose = () => {
    setEditingId(null);
    form.resetFields();
    onClose?.();
  };

  return (
    <Modal
      open={open}
      title={<span style={{ fontFamily: 'CustomFont' }}>{title}</span>}
      onCancel={handleClose}
      footer={null}
      width={520}
      destroyOnClose
      styles={{ body: { fontFamily: 'CustomFont' } }}
    >
      <div className="work-panel-form">
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            name={nameField}
            label={<span style={{ fontFamily: 'CustomFont' }}>{nameLabel}</span>}
            rules={[{ required: true, message: 'الزامی است' }]}
          >
            <Input style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          {extraFormFields}
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            className="bg-green-600 hover:bg-green-700 border-0 mb-4"
            style={{ fontFamily: 'CustomFont' }}
          >
            افزودن
          </Button>
        </Form>
        <Spin spinning={loading}>
          {items.length === 0 && !loading ? (
            <Empty description="موردی نیست" />
          ) : (
            <List
              dataSource={items}
              renderItem={(item) => (
                <List.Item
                  style={{ fontFamily: 'CustomFont' }}
                  actions={[
                    editingId === item.id ? (
                      <>
                        <Button
                          key="ok"
                          type="text"
                          icon={<CheckOutlined />}
                          loading={editSaving}
                          onClick={() => saveEdit(item.id)}
                        />
                        <Button
                          key="x"
                          type="text"
                          icon={<CloseOutlined />}
                          onClick={() => setEditingId(null)}
                        />
                      </>
                    ) : (
                      <>
                        <Button
                          key="e"
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingId(item.id);
                            setEditingName(getItemLabel(item));
                          }}
                        />
                        <Popconfirm
                          key="d"
                          title="حذف"
                          description={`آیا از حذف «${getItemLabel(item) || 'این مورد'}» اطمینان دارید؟`}
                          okText="بله، حذف کن"
                          cancelText="لغو"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => handleDelete(item.id)}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deletingId === item.id}
                          />
                        </Popconfirm>
                      </>
                    ),
                  ]}
                >
                  {editingId === item.id ? (
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  ) : renderItemLabel ? (
                    renderItemLabel(item)
                  ) : coloredBadges && (item.label_color || item.color) ? (
                    <Tag
                      color={item.label_color || item.color}
                      style={{ fontFamily: 'CustomFont', margin: 0 }}
                    >
                      {getItemLabel(item)}
                    </Tag>
                  ) : (
                    getItemLabel(item)
                  )}
                </List.Item>
              )}
            />
          )}
        </Spin>
      </div>
    </Modal>
  );
};

export default WorkManageEntityModal;
